import {
  Box,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { PlatformRequirementIssueStatus } from '@client/graphql/__generated__/types';
import { lowerCase, startCase } from 'lodash';
import { TriangleAlertIcon } from 'lucide-react';
import { FC, MouseEvent } from 'react';
import { CompliancePlatformRequirementIssue } from './types';

interface DocumentPlatformRequirementIssuesListProps {
  issues: CompliancePlatformRequirementIssue[];
  onIgnore?: (issue: CompliancePlatformRequirementIssue) => void;
  onUndoIgnore?: (issue: CompliancePlatformRequirementIssue) => void;
  isIgnoring?: boolean;
}

const formatAllowedValue = (value: string): string =>
  value.includes('_') ? startCase(lowerCase(value)) : value;

const getExpectedLabel = (
  issue: CompliancePlatformRequirementIssue
): string => {
  const allowedValues = [
    ...issue.allowedDocumentCategories,
    ...issue.allowedForms,
  ];

  return allowedValues.length > 0
    ? allowedValues.map(formatAllowedValue).join(', ')
    : 'Configured checklist requirement';
};

/**
 * Right-rail card of SkySlope checklist mismatch blockers for the selected
 * document. These are platform-placement issues, separate from validation
 * suggestions, but use the same ignore/undo affordance.
 */
export const DocumentPlatformRequirementIssuesList: FC<
  DocumentPlatformRequirementIssuesListProps
> = ({ issues, onIgnore, onUndoIgnore, isIgnoring = false }) => {
  if (issues.length === 0) {
    return null;
  }

  const heading =
    issues.length === 1
      ? '1 checklist mismatch'
      : `${issues.length} checklist mismatches`;

  return (
    <Box
      bg="whiteAlpha.50"
      borderColor="whiteAlpha.100"
      borderRadius="10px"
      borderWidth={1}
      p={5}
    >
      <VStack align="stretch" spacing={2}>
        <Heading
          color="compliance.status.warning"
          fontSize="lg"
          fontWeight="normal"
          size="headline"
        >
          {heading}
        </Heading>

        <VStack align="stretch" spacing={1}>
          {issues.map((issue) => {
            const isIgnored =
              issue.status === PlatformRequirementIssueStatus.IGNORED_BY_AGENT;
            const isResolved =
              isIgnored ||
              issue.status ===
                PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER ||
              issue.status === PlatformRequirementIssueStatus.RESUBMITTED;
            const canToggle =
              issue.isAgentActionable &&
              (issue.status === PlatformRequirementIssueStatus.OPEN ||
                isIgnored);
            const expectedLabel = getExpectedLabel(issue);

            const handleIgnore = (event: MouseEvent): void => {
              event.stopPropagation();
              if (!isIgnoring) {
                onIgnore?.(issue);
              }
            };

            const handleUndoIgnore = (event: MouseEvent): void => {
              event.stopPropagation();
              if (!isIgnoring) {
                onUndoIgnore?.(issue);
              }
            };

            return (
              <Flex
                key={issue.publicIssueId}
                align="flex-start"
                borderRadius="6px"
                gap={2}
                mx={-2}
                opacity={isResolved ? 0.6 : undefined}
                px={2}
                py={1}
              >
                <Icon
                  as={TriangleAlertIcon}
                  boxSize="16px"
                  color={
                    isResolved
                      ? 'compliance.text.muted'
                      : 'compliance.status.warning'
                  }
                  flexShrink={0}
                  mt={1}
                />
                <Flex direction="column" gap={1} minW={0}>
                  <Text
                    color={
                      isResolved
                        ? 'compliance.text.muted'
                        : 'compliance.text.primary'
                    }
                    fontSize="sm"
                    fontWeight="medium"
                    textDecoration={isResolved ? 'line-through' : undefined}
                  >
                    Wrong checklist item
                  </Text>
                  <VStack
                    align="stretch"
                    color="whiteAlpha.600"
                    fontSize="sm"
                    spacing={0.5}
                  >
                    <Text>Attached: {issue.attachedDocumentName}</Text>
                    <Text>Checklist item: {issue.checklistItemName}</Text>
                    <Text>Expected: {expectedLabel}</Text>
                    {canToggle &&
                      (isIgnoring ? (
                        <Flex align="center" gap={1.5}>
                          <Spinner size="xs" />
                          <Text>{isIgnored ? 'Undoing' : 'Ignoring'}</Text>
                        </Flex>
                      ) : isIgnored ? (
                        <Text
                          _hover={{ color: 'compliance.text.secondary' }}
                          aria-label="Undo ignore checklist mismatch"
                          as="button"
                          cursor="pointer"
                          textAlign="left"
                          textDecoration="underline"
                          type="button"
                          onClick={handleUndoIgnore}
                        >
                          Undo
                        </Text>
                      ) : (
                        <Text
                          _hover={{ color: 'compliance.text.secondary' }}
                          aria-label="Ignore checklist mismatch"
                          as="button"
                          cursor="pointer"
                          textAlign="left"
                          textDecoration="underline"
                          type="button"
                          onClick={handleIgnore}
                        >
                          Ignore
                        </Text>
                      ))}
                  </VStack>
                </Flex>
              </Flex>
            );
          })}
        </VStack>
      </VStack>
    </Box>
  );
};
